import { Endpoints } from "../helpers/Endpoints";
import * as APIModels from "../models";
import { authenticationService } from "../services/authentication.service";

export const fileService = {
    get,
    upload,
    thirdParty,
};

/**
 * Retrieves a document from an S3 bucket, by returning the url.
 *
 * @param {Object} params - Parameters for the document retrieval.
 * @param {string} params.submissionId - The submission ID.
 * @param {string} params.documentId - The document ID.
 * @param {string | null} params.source - Optional source information (can be null).
 * @returns {Promise<Response>} A Promise that resolves to the HTTP Response.
 *                              This will be used elsewhere to retrieve the document
 *                              as a url to the blob.
 */
function get({
    submissionId,
    documentId,
    source,
}: {
    submissionId: string;
    documentId: string;
    source: string | null;
}): Promise<Response> {
    const currentUser = authenticationService.currentUserValue;
    if (currentUser != null) {
        const requestOptions = {
            method: "GET",
            //  mode: "cors" as RequestMode,
            headers: { Authorization: `${currentUser.authToken}` },
        };

        console.log(
            `${
                Endpoints.FileManager
            }?key=${submissionId}/${documentId}&source=${source ?? ""}`
        );

        return fetch(
            `${
                Endpoints.FileManager
            }?key=${submissionId}/${documentId}&source=${source ?? ""}`,
            requestOptions
        );
    }

    return fetch(`${Endpoints.FileManager}`);
}

/**
 * Uploads a document to an S3 bucket using a pre-signed URL.
 *
 * @param {Object} params - Parameters for the document upload.
 * @param {any} params.file - The file(s) to be uploaded. Can be a single file or an array of files.
 * @param {string} params.filename - The name of the file to be uploaded.
 * @param {string} params.uuid - The optional UUID of the document being uploaded.
 * @returns {Promise<Response>} A Promise that resolves to the HTTP Response.
 */
async function upload(
    params: {
        /**  */
        fileServiceUrl: string;
        file: any;
        filename: string;
        uuid?: string;
    } = { fileServiceUrl: Endpoints.FileManager } as any
): Promise<Response> {
    const currentUser = authenticationService.currentUserValue;
    if (currentUser != null) {
        console.log("UUID", currentUser.userID ?? params.uuid);
        const urlResponse = await uploadUrl({
            fileServiceUrl: params.fileServiceUrl,
            uid: currentUser.userID ?? params.uuid,
            key: params.filename,
        });

        const UrlData = await urlResponse.json();

        let data = null;
        data = new FormData();
        const multipleFiles =
            Object.prototype.toString.call(params["file"]) === "[object Array]";
        if (params["file"]) {
            if (multipleFiles) {
                for (const item of params["file"]) {
                    data.append("file", item as any);
                }
            }
        }

        let contentType = "application/octet-stream";
        if (params.filename.toLowerCase().indexOf(".pdf") > 0)
            contentType = "application/pdf";

        const requestOptions = {
            method: "PUT",
            //mode: "no-cors" as RequestMode,
            headers: {
                "Content-Type": contentType,
            },

            body: multipleFiles ? data : params["file"],
        };

        return fetch(
            UrlData.upload_url,
            // `https://xx0yydmcx3.execute-api.us-east-1.amazonaws.com/Prod?uid=${currentUser.userID}&key=${params.filename}`,
            requestOptions
        );
    }

    return fetch(`${params.fileServiceUrl}`, { method: "GET" });
}

/**
 * Retrieves a pre-signed URL for uploading a document to an S3 bucket.
 *
 * @param {Object} params - Parameters for generating the upload URL.
 * @param {string} params.uid - The user ID associated with the upload.
 * @param {string} params.key - The key or filename for the document being uploaded.
 * @returns {Promise<Response>} A Promise that resolves to the HTTP Response containing the pre-signed URL.
 */
function uploadUrl({
    uid,
    key,
    fileServiceUrl,
}: {
    uid: string;
    key: string;
    fileServiceUrl: string;
}) {
    const currentUser = authenticationService.currentUserValue;
    console.log("File Service URL", fileServiceUrl);
    if (currentUser != null) {
        const requestOptions = {
            method: "POST",
            mode: "cors" as RequestMode,
            headers: {
                Authorization: `${currentUser.authToken}`,
                "Content-Type": "application/json",
            },
        };

        return fetch(`${fileServiceUrl}?uid=${uid}&key=${key}`, requestOptions);
    }

    return fetch(`${fileServiceUrl}`);
}

/**
 * Uploads documents to the eFileMadeEasy Queue using a third-party API.
 *
 * @param {Object} params - Parameters for uploading documents to the queue.
 * @param {string} params.url - The URL of the third-party API for uploading documents.
 * @param {string} params.attorneyId - The attorney ID associated with the documents.
 * @param {string} params.filename - The name of the document being uploaded.
 * @param {any} params.file - The file or an array of files to be uploaded.
 * @returns {Promise<Response>} A Promise that resolves to the HTTP Response.
 */
function thirdParty(
    params: {
        fileServiceUrl: string;
        url: string;
        attorneyId: string;
        filename: string;
        file: any;
    } = {} as any
): Promise<Response> {
    const currentUser = authenticationService.currentUserValue;
    if (currentUser != null) {
        const data = new FormData();

        if (params.file) {
            if (Array.isArray(params.file)) {
                for (const item of params.file) {
                    data.append("Package", item);
                }
            } else {
                data.append("Package", params.file);
            }
        }

        data.append("AttorneyId", params.attorneyId);

        const requestOptions: RequestInit = {
            method: "POST",
            mode: "cors",
            headers: {
                Authorization: `Bearer ${currentUser.authToken}`,
                "X-User-AccessToken": currentUser.accessToken,
                "X-User-RefreshToken": currentUser.refreshToken,
            },
            body: data,
        };

        console.log(requestOptions);

        return fetch(params.url, requestOptions);
    }

    return fetch(params.fileServiceUrl, { method: "GET" });
}
